'use client';

import React from 'react';
import Image from 'next/image';
import HeroImage from '@/images/hero_darkmode.png';
import LightHeroImage from '@/images/hero_lightmode.png';
import HeroLight from '@/images/hero_light.png';

export function ComingSoonHero() {
  const handleGetAccess = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Trigger the LaunchListPanelOpener by adding the query param
    const url = new URL(window.location.href);
    url.searchParams.set('waitlist', '1');
    window.history.pushState({}, '', url);
    // Dispatch custom event to trigger the panel
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <>
      <div className="relative isolate min-h-screen flex items-start justify-center overflow-hidden bg-hero font-mulish text-snowbank">
        {/* Background images */}
        <div className="absolute inset-0 z-[-1]">
        {/* Dark background image */}
        <Image
          unoptimized
          placeholder="blur"
          priority
          quality={100}
          alt="Hero dark background"
          src={HeroImage}
          className="hidden dark:block w-full h-full object-cover 3xl:object-contain object-center"
          style={{ transform: 'scale(1.15)' }}
        />
        {/* Light background image */}
        <Image
          unoptimized
          placeholder="blur"
          priority
          quality={100}
          alt="Hero light background"
          src={LightHeroImage}
          className="block dark:hidden w-full h-full object-cover 3xl:object-contain object-center"
          style={{ transform: 'scale(1.15)' }}
        />
        {/* Light effect - layered on top */}
        <Image
          src={HeroLight}
          alt="Hero light effect"
          width={1200}
          height={800}
          unoptimized
          priority
          className="absolute top-[-5%] left-[18%] z-10 hidden dark:block"
          style={{ mixBlendMode: 'hard-light', transform: 'scale(1.15)' }}
        />
      </div>

      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-900/20 dark:to-gray-900/40 z-0" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-4xl px-6 pt-16 sm:pt-20 pb-24 sm:pb-32 text-center">
        {/* Main heading */}
        <h1 className="text-2xl sm:text-4xl leading-relaxed font-semibold dark:font-bold max-w-4xl mx-auto drop-shadow-sm mb-6 sm:mb-10">
          <span className="bg-clip-text text-transparent" style={{backgroundImage: 'linear-gradient(to right, var(--color-logo-color-1), var(--color-logo-color-2), var(--color-logo-color-3), var(--color-logo-color-4), var(--color-logo-color-5))'}}>
            Prototype fast.<br className="sm:hidden" /> Ship reliable robotics.
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-xl mx-auto mb-8 sm:mb-20">
          Prompt‑to‑code robotics software—build without the hacks
        </p>

        {/* Coming Soon tag - now clickable with liquid shimmer effect */}
        <div className="mt-8 mb-8">
          <button
            onClick={handleGetAccess}
            className="btn-signup relative inline-flex items-center rounded-full bg-gray-700 dark:bg-meteor-shower/90 px-8 py-3 text-lg font-bold text-white shadow-2xl backdrop-blur-sm transition-all transform hover:scale-105 active:scale-95 cursor-pointer overflow-hidden ring-2 ring-gray-600 dark:ring-venetian-night/50"
          >
            {/* Animated liquid shimmer background - always visible */}
            <span className="absolute inset-0 opacity-70 btn-signup:hover:opacity-90 transition-opacity duration-500 overflow-hidden">
              <span className="absolute inset-y-0 left-0 right-0 w-[500%] -ml-[200%] bg-gradient-to-l from-transparent via-white/30 to-transparent animate-[wave_3s_ease-in-out_infinite]" />
            </span>
            
            {/* Glow effect - changes color on hover, stays within button bounds */}
            <span className="glow-effect absolute inset-0 bg-gradient-to-r from-meteor-shower/30 to-venetian-night/30 rounded-full opacity-0 transition-all duration-500" />
            
            {/* Text */}
            <span className="relative z-10">Sign Up Now</span>
          </button>
        </div>
        
        <style jsx>{`
          .btn-signup:hover .glow-effect {
            opacity: 1;
            background: linear-gradient(to right, rgb(81, 58, 250), rgb(106, 87, 255));
          }
          
          @keyframes wave {
            0%, 100% {
              transform: translateX(100%) skewX(-10deg);
            }
            50% {
              transform: translateX(-100%) skewX(10deg);
            }
          }
        `}</style>

        {/* Additional info */}
        <p className="mt-80 sm:mt-96 text-base text-gray-600 dark:text-gray-400">
          Join the waitlist to get early access!
        </p>
      </div>
    </div>

    {/* Feature highlights section - separate background - hidden for now */}
    <div className="hidden relative bg-secondary font-mulish py-24">
      <div className="mx-auto max-w-5xl px-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div className="backdrop-blur-sm bg-white/40 dark:bg-gray-900/40 p-8 rounded-xl ring-1 ring-gray-900/5 dark:ring-gray-100/5">
            <div className="text-4xl mb-4">🧠</div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">
              Advanced AI
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Cutting-edge machine learning and decision-making capabilities
            </p>
          </div>
          
          <div className="backdrop-blur-sm bg-white/40 dark:bg-gray-900/40 p-8 rounded-xl ring-1 ring-gray-900/5 dark:ring-gray-100/5">
            <div className="text-4xl mb-4">⚡</div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">
              Real-Time Performance
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Lightning-fast processing for time-critical applications
            </p>
          </div>
          
          <div className="backdrop-blur-sm bg-white/40 dark:bg-gray-900/40 p-8 rounded-xl ring-1 ring-gray-900/5 dark:ring-gray-100/5">
            <div className="text-4xl mb-4">🛡️</div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">
              Safe & Reliable
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Built-in safety guarantees for mission-critical deployments
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
